<template>
  <v-card>
    <v-app-bar
      absolute
      color="white"
      elevate-on-scroll
      scroll-target="#risk_assessment_form"
    >
      <v-container>
        <v-row align="center">
          <v-col class="title">
            {{ assessment.name }}
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <span
              style="font-size: 16px; font-weight: normal;"
            >
              {{ $t('risk_assessment.form.risk_level.label') }}:
              <span
                style="font-weight: bold;"
                v-if="riskAssessment.score || riskAssessment.max_score"
                :style="{ color: riskLevel?.color || '#000000' }"
              >
                {{ riskLevel?.name }} ({{ riskAssessment.score.toFixed(2) }})
              </span>
            </span>
          </v-col>
          <v-col cols="auto">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <div
                  style="position: relative; height: 25px;"
                  v-on="on"
                  v-bind="attrs"
                >
                  <v-icon
                    :style="{
                      position: 'absolute',
                      top: '-20px',
                      left: getArrowPosition() + 'px',
                      fontSize: '25px'
                    }"
                  >
                    mdi-arrow-down
                  </v-icon>
                  <span
                    v-for="(block, index) in riskVisualizeBlocks"
                    :key="index"
                    :style="{ backgroundColor: block.color, width: '25px', height: '25px', display: 'inline-block' }"
                  />
                </div>
              </template>
              <span>
                <div
                  v-for="(level, key) in assessment?.levels"
                  :key="key"
                >
                  {{ level.name }}: >= {{ level.score }}
                </div>
              </span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </v-app-bar>
    <v-card-text>
      <v-sheet
        id="risk_assessment_form"
        class="overflow-y-auto"
        style="max-height: calc(100vh - 64px - 66px - 82px - 160px);padding-top: 48px;"
      >
        <v-container>
          <RiskAssessmentFactor
            v-for="(factor) in assessment?.factors"
            :key="factor.id"
            :factor="factor"
            @onFactorScoreUpdate="onFactorScoreUpdate"
          />
        </v-container>
      </v-sheet>
    </v-card-text>
    <v-card-actions>
      <v-row>
        <v-col style="text-align: center;">
          <v-btn
            color="primary"
            @click.prevent="onSubmit()"
            :disabled="$v.$invalid"
          >
            {{ $t('risk_assessment.buttons.save') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import RiskAssessmentFactor from './RiskAssessmentFactor.vue'
import { required } from 'vuelidate/lib/validators'

export default {
  props: {
    assessment: {
      type: Object,
      default: null
    }
  },
  components: {
    RiskAssessmentFactor
  },
  watch: {
    assessment: {
      immediate: true,
      handler () {
        this.factorScores = {}
        this.riskAssessment = {
          completed: false,
          score: 0,
          max_score: 0,
          min_score: 0
        }
      }
    }
  },
  data () {
    return {
      factorScores: {},
      riskAssessment: {
        completed: false,
        score: 0,
        max_score: 0,
        min_score: 0
      }
    }
  },
  validations () {
    return {
      riskAssessment: {
        completed: {
          required,
          isCompleted (value) {
            return value
          }
        }
      }
    }
  },
  computed: {
    riskVisualizeBlocks () {
      const levels = this.assessment?.levels || []
      if (!levels.length) {
        return [
          { color: '#2C9E4A' },
          { color: '#EC8C43' },
          { color: '#F92F2F' }
        ]
      }

      return levels.map((level, index) => ({
        color: this.getLevelColor(index, levels.length)
      }))
    },
    riskLevel () {
      if (!this.assessment?.levels?.length) return null
      const sortedLevels = [...this.assessment.levels].sort((a, b) => a.score - b.score)
      let selectedLevel = sortedLevels[0]
      for (const level of sortedLevels) {
        if (this.riskAssessment.score >= level.score) {
          selectedLevel = level
        }
      }
      const index = sortedLevels.findIndex(l => l === selectedLevel)
      selectedLevel.color = this.getLevelColor(index, sortedLevels.length)
      return selectedLevel
    }
  },
  methods: {
    getLevelColor (index, totalLevels) {
      const colors = {
        3: ['#2C9E4A', '#EC8C43', '#F92F2F'],
        4: ['#2C9E4A', '#8BC34A', '#FF9800', '#F92F2F'],
        5: ['#2C9E4A', '#66BB6A', '#FF9800', '#FF5722', '#F92F2F'],
        6: ['#2C9E4A', '#66BB6A', '#FFCC00', '#FF9800', '#FF5722', '#F92F2F'],
        7: ['#1B5E20', '#4CAF50', '#8BC34A', '#FFCC00', '#FF9800', '#FF5722', '#D32F2F']
      }
      const palette = colors[totalLevels] || colors[5]
      return palette[index] || '#999999'
    },
    getArrowPosition () {
      const score = this.riskAssessment.score || 0
      const levels = this.assessment?.levels || []
      if (!levels.length) return 0
      const sortedLevels = [...levels].sort((a, b) => a.score - b.score)
      let blockIndex = 0
      for (let i = 0; i < sortedLevels.length; i++) {
        if (score >= sortedLevels[i].score) {
          blockIndex = Math.min(i, sortedLevels.length - 1)
        }
      }
      return blockIndex * 25
    },
    onFactorScoreUpdate ({ factor, completed, score_scaled, max_score_scaled, min_score_scaled, question_scores }) {
      this.factorScores[factor.id] = {
        completed,
        score: score_scaled,
        max: max_score_scaled,
        min: min_score_scaled,
        question_scores
      }

      const currentTemplateFactorIds = this.assessment?.factors?.map(f => f.id) || []
      this.riskAssessment.completed = currentTemplateFactorIds.every(id => this.factorScores[id]?.completed === true)
      this.riskAssessment.score = currentTemplateFactorIds.reduce((acc, id) => acc + (this.factorScores[id]?.score || 0), 0)
      this.riskAssessment.max_score = currentTemplateFactorIds.reduce((acc, id) => acc + (this.factorScores[id]?.max || 0), 0)
      this.riskAssessment.min_score = currentTemplateFactorIds.reduce((acc, id) => acc + (this.factorScores[id]?.min || 0), 0)
      this.updateScore()
    },
    updateScore () {
      this.$emit('onAssessmentScoreUpdate', {
        assessment: this.assessment,
        completed: this.riskAssessment.completed,
        level: this.riskLevel,
        score_scaled: this.riskAssessment.score,
        min_score_scaled: this.riskAssessment.min_score,
        max_score_scaled: this.riskAssessment.max_score,
        factor_scores: this.factorScores
      })
    },
    onSubmit () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return
      }

      this.updateScore()
      this.$emit('onSubmit')
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.updateScore()
    })
  }
}
</script>
